import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import './ConnectStep.scss';

import { Button, Input, Notification } from '../design-system/components';
import { PASSWORD_MANAGER_OPT_OUT } from '../design-system/components/Input';
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator';

import { FieldGuideConnectResult, isFieldGuideConnectFailure } from '../../Services/FieldGuideService';
import { ApplicationState, SimplifiedThunkDispatch } from '../../Store';
import {
    FieldGuideConnectionState,
    GetFieldGuideConnectionState,
    KnownAction as FieldGuideKnownAction,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';

// FieldGuideScopes.Required, per the Technical Design wiki's "Scope & revocation validation (KTD11)".
export const REQUIRED_FIELD_GUIDE_SCOPES = [
    'companies:read',
    'engagements:read',
    'requests:read',
    'requests.files:read',
    'requests.files:write',
];

// Placeholder copy (per the story) for each 422/503 `reason` the connect endpoint can return. Any
// reason not covered here — including a bodyless 500 or a network failure, where `reason` is
// undefined — falls back to GENERIC_ERROR_MESSAGE.
const REASON_MESSAGES: Record<string, string> = {
    invalid_token: 'FieldGuide didn\'t accept this token. Check that you copied the whole token and that it hasn\'t been deleted.',
    not_a_pat: 'This isn\'t a personal access token. Create one in FieldGuide under My Profile > Integrations.',
    token_expiring: 'This token expires too soon to use. Create a PAT with a later expiration date.',
    portal_unavailable: 'We couldn\'t complete the connection because of a problem on our side. Please try again in a few minutes, and contact support if the problem continues.',
    unavailable: 'FieldGuide is temporarily unavailable. Please try again in a few minutes.',
};

const GENERIC_ERROR_MESSAGE = 'FieldGuide couldn\'t complete the connection. Please try again, and contact support if the problem continues.';

function getErrorMessage(reason: string | undefined, missingScopes: string[] | undefined): string {
    if (reason === 'insufficient_scope') {
        return `This token is missing required permissions: ${(missingScopes || []).join(', ')}. Create a PAT that grants every required scope.`;
    }

    return (reason && REASON_MESSAGES[reason]) || GENERIC_ERROR_MESSAGE;
}

type FieldGuideConnectStepView = 'connect' | 'pat';

type InjectedReduxState = {
    connectionState: FieldGuideConnectionState;
};

type InjectedActionCreators = {
    // Dispatched (not bound via bindActionCreators) so the thunk's own Promise<FieldGuideConnectResult>
    // return value reaches the caller, letting the form render the specific rejection reason inline.
    ConnectWithPat: (token: string) => Promise<FieldGuideConnectResult>;
};

type FieldGuideConnectStepProps = {
    // Shown above Status/Reconnect banners when the connection needs reconnecting; undefined otherwise.
    reconnectBanner?: React.ReactNode;
};

type Props = InjectedReduxState & InjectedActionCreators & FieldGuideConnectStepProps;

export const ConnectStepComponent: React.FC<Props> = (props): React.ReactElement => {
    const { connectionState, reconnectBanner, ConnectWithPat } = props;

    const [view, setView] = React.useState<FieldGuideConnectStepView>('connect');
    const [token, setToken] = React.useState<string>('');
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

    const onSubmit = async (): Promise<void> => {
        if (submitting || token.trim() === '') {
            return;
        }

        setSubmitting(true);
        setErrorMessage(undefined);

        const result: FieldGuideConnectResult = await ConnectWithPat(token);

        // The token field is cleared after any submit completes, success or failure, and is never
        // retained outside this component's own state.
        setToken('');
        setSubmitting(false);

        if (isFieldGuideConnectFailure(result)) {
            setErrorMessage(getErrorMessage(result.failure.reason, result.failure.missingScopes));
        }
    };

    if (connectionState === FieldGuideConnectionState.Connected) {
        return (
            <div className={'field-guide-connect-step'}>
                <Notification type={'success'} heading={'Connected.'} description={''} dismissable={false} />
            </div>
        );
    }

    if (view === 'pat') {
        return (
            <div className={'field-guide-connect-step field-guide-pat-form'}>
                {errorMessage && (
                    <Notification type={'danger'} heading={errorMessage} description={''} dismissable={false} />
                )}
                <div className={'page-stack page-stack--tight'}>
                    <label className={'field-guide-pat-form__label'} htmlFor={'field-guide-pat-token'}>
                        Personal access token
                    </label>
                    <Input
                        id={'field-guide-pat-token'}
                        name={'fieldguide-api-token'}
                        // Masked, but an API token rather than a site login: opt out so managers never offer or save it.
                        type={'password'}
                        {...PASSWORD_MANAGER_OPT_OUT}
                        placeholder={'Paste your FieldGuide PAT'}
                        value={token}
                        disabled={submitting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToken(e.currentTarget.value)}
                        error={!!errorMessage}
                    />
                </div>
                <div className={'field-guide-pat-form__help page-help-text'}>
                    <p>
                        {'Create a PAT in FieldGuide under '}<strong>{'My Profile > Integrations'}</strong>{', then '}
                        <strong>{'+Add custom integration'}</strong>{'. Give it a name, an expiration date, and these scopes:'}
                    </p>
                    <ul className={'field-guide-pat-form__scopes'}>
                        {REQUIRED_FIELD_GUIDE_SCOPES.map(scope => (
                            <li key={scope}><code>{scope}</code></li>
                        ))}
                    </ul>
                    <p>
                        {'FieldGuide shows the token only until the creation dialog is closed. Tokens expire up to 1 year '}
                        {'after creation; the expiration defaults to 90 days.'}
                    </p>
                </div>
                <div className={'field-guide-pat-form__actions'}>
                    <Button
                        type={'floating'}
                        disabled={submitting}
                        onClick={() => {
                            setView('connect');
                            setErrorMessage(undefined);
                            setToken('');
                        }}
                    >
                        Back
                    </Button>
                    <Button
                        type={'primary'}
                        disabled={submitting || token.trim() === ''}
                        onClick={onSubmit}
                    >
                        Connect
                    </Button>
                </div>
                <LoadingIndicator active={submitting} showSpinner={true} centerIndicator={true} />
            </div>
        );
    }

    return (
        <div className={'field-guide-connect-step page-stack'}>
            {reconnectBanner}
            <Notification
                type={'info'}
                heading={''}
                description={'Data is sent to Fieldguide only when you choose to export a collection. Nothing is shared automatically or in the background.'}
                dismissable={false}
            />
            <div className={'field-guide-connect-step__actions'}>
                <Button type={'floating'} onClick={() => setView('pat')}>
                    Use a PAT instead?
                </Button>
            </div>
        </div>
    );
};

export const ConnectStep = connect<InjectedReduxState, InjectedActionCreators, FieldGuideConnectStepProps, ApplicationState>(
    (appState: ApplicationState) => {
        return {
            connectionState: GetFieldGuideConnectionState(appState),
        };
    },
    (dispatch: SimplifiedThunkDispatch<FieldGuideKnownAction>) => {
        return {
            ConnectWithPat: (token: string) => dispatch(FieldGuideStoreActions.ConnectWithPat(token)),
        };
    }
)(ConnectStepComponent);
